import type {
  MinimumBetSchema,
  PrizeSchema,
  ProgressSchema,
  PublicCampaignSchema,
  RaffleSchema,
  RaffleTaskSchema,
  RaffleWinnerSchema,
} from "@/types";

/**
 * The rendered Raffle view object the components consume.
 *
 * NOT a wire schema — assembled (see `./assemble`) from two streams: `campaigns` (the structural
 * `PublicCampaignSchema.raffle` embed + the place-ranged `prizes`, pushed once) ⋈ `raffle_progress`
 * (the player's tickets and per-task progress, volatile). Fields are composed from the generated
 * schemas so a backend change surfaces here.
 */

/** One task rule folded with the player's live progress on it. */
export interface RaffleTask
  extends Pick<
    RaffleTaskSchema,
    "id" | "order" | "kind" | "title" | "tickets_per_completion" | "threshold"
  > {
  /** Cap on the tickets this task can earn; `0` = uncapped. */
  max_tickets: number;
  /** Tickets earned on this task so far — `0` until the progress stream lands. */
  tickets: number;
  progress: ProgressSchema | null;
}

export interface Raffle {
  campaign: PublicCampaignSchema;
  raffle: RaffleSchema;
  minimum_bet: Pick<MinimumBetSchema, "value" | "currency"> &
    Partial<Pick<MinimumBetSchema, "display">>;
  /** Place-ranged prizes (`place_from`–`place_to`), lowest place first. */
  prizes: PrizeSchema[];
  tasks: RaffleTask[];
  /** The player's ticket total; `null` until `raffle_progress` has pushed (never "0 tickets"). */
  total_tickets: number | null;
  winners: RaffleWinnerSchema[];
  eligible_games: string[] | "ALL";
  /** Total eligible-games count (list may be a preview subset). */
  eligible_games_count?: number;
}

/**
 * The three states a raffle is visibly in. The middle one has no equivalent in the other types:
 * the campaign is over (or the draw lock is taken) but nobody has won yet — it must not read as
 * either "still earning" or "finished with results".
 */
export type RafflePhase = "live" | "awaiting-draw" | "drawn";

/** `drawn` is the backend's own predicate (`draw_seed` written) — not the lock, not the status. */
export function rafflePhase(raffle: Raffle): RafflePhase {
  if (raffle.raffle.drawn) return "drawn";
  if (raffle.raffle.locked_for_prize_draw || raffle.campaign.status === "FI") {
    return "awaiting-draw";
  }
  return "live";
}

/**
 * When the draw happens. The backend writes `draw_timestamp` only when the draw itself lands, so
 * until then it is null and the draw is due when entries close: the campaign's end.
 */
export function drawDate(raffle: Raffle): Date {
  const ts = raffle.raffle.draw_timestamp;
  const date = new Date(ts ?? raffle.campaign.end);
  return Number.isFinite(date.getTime()) ? date : new Date(raffle.campaign.end);
}

/** How many of a prize the draw hands out: the width of its place bracket (4 places on €20 Cash
 *  = 4 of them). A prize that cannot be pinned to a bracket counts once. */
export const prizeQuantity = (prize: PrizeSchema): number =>
  prize.place_from != null && prize.place_to != null
    ? Math.max(1, prize.place_to - prize.place_from + 1)
    : 1;

/** Every prize on offer, brackets counted out. */
export const totalPrizeCount = (raffle: Raffle): number =>
  raffle.prizes.reduce((sum, prize) => sum + prizeQuantity(prize), 0);

/** A task that has reached its ticket cap and can earn nothing more. */
export const isTaskMaxed = (task: RaffleTask): boolean =>
  task.max_tickets > 0 && task.tickets >= task.max_tickets;

/** The winners that are the reader's own: a player can win several places (the draw hands out
 *  brackets one after another from the same ticket pool), and the list is in payout order. */
export const viewerWinnings = (
  raffle: Raffle,
  viewerHash: string | null
): RaffleWinnerSchema[] =>
  viewerHash ? raffle.winners.filter((w) => w.player === viewerHash) : [];

/** Who the draw paid this prize to: the winners on its place bracket. */
export const prizeWinners = (
  raffle: Raffle,
  prize: PrizeSchema
): RaffleWinnerSchema[] =>
  raffle.winners.filter(
    (w) => w.place_from === prize.place_from && w.place_to === prize.place_to
  );

/** The winner's name as the design shows it, "xy***": the first two letters of the masked username
 *  the backend stores ("pla**", "*****"), then three stars whatever its length — so every chip is
 *  the same width and the stored mask's length says nothing about the name. */
export const maskedWinnerName = (username: string): string =>
  `${Array.from(username.replace(/\*+$/, "")).slice(0, 2).join("")}***`;
